// Motivační nástěnka sales nad plánem volání (15. 10. 2026, Láďa, schválený mockup).
// Od začátku roku (kontakty vytvořené od 1. 1. včetně převzatých z Notionu, které byly
// navolané): kolik kontaktů, kolik zkušebních, kolik uzavřených, obrat z uzavřených
// a kolik má sales na bonusech (200 Kč za uzavřený deal).
// 15. 10. 2026 (schválený mockup): výběr období Letos (výchozí), Měsíc, Rok, Celkem,
// u měsíce a roku šipky. Na telefonu přepínač pod nadpisem a šipky pod ním.
import React, { useEffect, useState } from "react";
import { Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import { salesGet } from "./salesApi.js";

const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ")} Kč`;
const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const TYPY = [{ key: "letos", label: "Letos" }, { key: "mesic", label: "Měsíc" }, { key: "rok", label: "Rok" }, { key: "celkem", label: "Celkem" }];
const dnesMesic = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" }).substring(0, 7);
const posunMesic = (m, o) => { const [y, mm] = m.split("-").map(Number); const d = new Date(Date.UTC(y, mm - 1 + o, 1)); return d.toISOString().substring(0, 7); };
const mesicText = (m) => { const [y, mm] = m.split("-").map(Number); return `${MESICE[mm - 1]} ${y}`; };
const velke = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const sklon = (n, a, b, c) => (n === 1 ? a : n >= 2 && n <= 4 ? b : c);

function Bunka({ popisek, hodnota, pod, zlata = false }) {
  return (
    <div className={`min-w-0 rounded-xl px-3 py-2.5 ${zlata ? "border border-brand-gold/50 bg-brand-gold/15" : "bg-white/10"}`}>
      <div className={`text-[11px] font-bold uppercase tracking-wider ${zlata ? "text-brand-gold" : "text-white/75"}`}>{popisek}</div>
      <div className="whitespace-nowrap text-xl font-bold tabular-nums sm:text-2xl">{hodnota}</div>
      <div className="truncate text-xs text-white/75">{pod}</div>
    </div>
  );
}

export default function SalesNastenka() {
  const [typ, setTyp] = useState("letos");
  const [mesic, setMesic] = useState(dnesMesic);
  const [rok, setRok] = useState(() => dnesMesic().substring(0, 4));
  const [d, setD] = useState(null);
  useEffect(() => {
    let ok = true;
    salesGet("nastenka", { obdobi: typ, mesic: typ === "mesic" ? mesic : undefined, rok: typ === "rok" ? rok : undefined })
      .then((x) => { if (ok) setD(x); }).catch(() => {});
    return () => { ok = false; };
  }, [typ, mesic, rok]);
  if (!d) return null;
  const b = d.bonusy || {};
  const aktualni = dnesMesic();
  const pilulka = typ === "mesic" ? mesicText(mesic) : typ === "rok" ? `rok ${rok}` : typ === "celkem" ? "za celou dobu" : `od 1. 1. ${d.rok}`;
  const sipky = typ === "mesic" || typ === "rok";
  const vpredVypnuto = typ === "mesic" ? mesic >= aktualni : rok >= aktualni.substring(0, 4);
  const posun = (o) => (typ === "mesic" ? setMesic((m) => posunMesic(m, o)) : setRok((r) => String(Number(r) + o)));
  return (
    <div className="rounded-2xl bg-brand-900 p-3 text-white shadow-glass sm:p-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-[1_1_260px] items-center gap-2">
          <Trophy className="h-5 w-5 shrink-0 text-brand-gold" />
          <span className="text-base font-bold">Moje výsledky</span>
          <span className="whitespace-nowrap rounded-full bg-white/15 px-2.5 py-1 text-xs font-bold text-white">{pilulka}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Období">
          {TYPY.map((t) => (
            <button key={t.key} type="button" onClick={() => setTyp(t.key)} aria-pressed={typ === t.key}
              className={`min-h-[36px] rounded-full px-3.5 text-sm font-bold ${typ === t.key ? "bg-white text-brand-900" : "bg-white/10 text-white hover:bg-white/20"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {sipky && (
          <div className="flex min-w-[220px] items-center justify-between gap-1 rounded-full bg-white/10 p-1">
            <button type="button" onClick={() => posun(-1)} aria-label="Předchozí období" title="Předchozí období"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"><ChevronLeft className="h-[18px] w-[18px]" /></button>
            <span className="px-2 text-center text-sm font-bold">{typ === "mesic" ? velke(mesicText(mesic)) : rok}</span>
            <button type="button" onClick={() => posun(1)} disabled={vpredVypnuto} aria-label="Následující období" title="Následující období"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30"><ChevronRight className="h-[18px] w-[18px]" /></button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        <Bunka popisek="Navoláno" hodnota={d.volala} pod={sklon(d.volala, "kontakt", "kontakty", "kontaktů")} />
        <Bunka popisek="Zkušební" hodnota={d.zkusebni} pod={sklon(d.zkusebni, "domluvený", "domluvené", "domluvených")} />
        <Bunka popisek="Uzavřeno" hodnota={d.uzavreno ?? 0} pod={sklon(d.uzavreno ?? 0, "nová klientka", "nové klientky", "nových klientek")} />
        <Bunka popisek="Přinesly firmě" hodnota={kc(d.prinesly)} pod="zaplacené faktury" />
        <Bunka zlata popisek="Moje bonusy" hodnota={kc(b.castka)} pod={b.n ? `${b.n} ${sklon(b.n, "deal", "dealy", "dealů")}` : "zatím žádný deal"} />
      </div>
    </div>
  );
}
